import { useCallback, useMemo } from 'react';
import type {
  ITopFiveApiV2EndpointsAuthLoginEndpointParams,
  ITopFiveApiV2EndpointsAuthLogoutEndpointParams,
} from '@/api/models';
import {
  getITopFiveApiV2EndpointsAuthLoginEndpointQueryKey,
  getITopFiveApiV2EndpointsAuthLogoutEndpointQueryKey,
} from '@/api/api.generated';
import { BASE_PATH } from '@/app/config/appConfig';

/**
 * Custom hook for handling authentication-related URLs using generated API client types
 */
export const useAuthUrls = () => {
  const createReturnUrl = useCallback((path?: string): string => {
    // If path provided, ensure it includes BASE_PATH; otherwise use current location
    const currentPath = path
      ? (path.startsWith(BASE_PATH) ? path : `${BASE_PATH}${path}`)
      : window.location.pathname;
    const fullReturnUrl = `${window.location.origin}${currentPath}`;
    return fullReturnUrl;
  }, []);

  const createLoginUrl = useCallback(
    (returnPath?: string): string => {
      const returnUrl = createReturnUrl(returnPath);
      // Use typed parameters from generated API client
      const params: ITopFiveApiV2EndpointsAuthLoginEndpointParams = {
        returnUrl,
      };
      const queryString = params.returnUrl
        ? `?returnUrl=${encodeURIComponent(params.returnUrl)}`
        : '';
      // Extract the path from the generated query key to avoid hardcoding
      const [loginPath] = getITopFiveApiV2EndpointsAuthLoginEndpointQueryKey();
      return `${loginPath}${queryString}`;
    },
    [createReturnUrl]
  );

  const createLogoutUrl = useCallback(
    (returnPath?: string): string => {
      const returnUrl = createReturnUrl(returnPath);
      // Use typed parameters from generated API client
      const params: ITopFiveApiV2EndpointsAuthLogoutEndpointParams = {
        returnUrl,
      };
      const queryString = params.returnUrl
        ? `?returnUrl=${encodeURIComponent(params.returnUrl)}`
        : '';
      // Extract the path from the generated query key to avoid hardcoding
      const [logoutPath] =
        getITopFiveApiV2EndpointsAuthLogoutEndpointQueryKey();
      return `${logoutPath}${queryString}`;
    },
    [createReturnUrl]
  );

  const redirectToLogin = useCallback(
    (returnPath?: string): void => {
      window.location.href = createLoginUrl(returnPath);
    },
    [createLoginUrl]
  );

  const redirectToLogout = useCallback(
    (returnPath?: string): void => {
      window.location.href = createLogoutUrl(returnPath);
    },
    [createLogoutUrl]
  );

  return useMemo(
    () => ({
      createReturnUrl,
      createLoginUrl,
      createLogoutUrl,
      redirectToLogin,
      redirectToLogout,
    }),
    [
      createReturnUrl,
      createLoginUrl,
      createLogoutUrl,
      redirectToLogin,
      redirectToLogout,
    ]
  );
};
